import type { LogContext, LoggerStrategy } from './logger-strategy.interface.js';

/**
 * Composite strategy that executes multiple logging strategies simultaneously.
 * Useful for logging to multiple destinations at once (e.g., console + remote monitoring).
 *
 * @example
 * ```typescript
 * import { LoggerService, CompositeStrategy, ColoredConsoleStrategy, JsonStrategy } from '@flux/logger';
 *
 * // Log to both console and JSON simultaneously
 * LoggerService.setStrategy(
 *   new CompositeStrategy([
 *     new ColoredConsoleStrategy(),
 *     new JsonStrategy()
 *   ])
 * );
 * ```
 */
export class CompositeStrategy implements LoggerStrategy {
  public constructor(private readonly strategies: LoggerStrategy[]) {}

  /**
   * Executes the log method on all registered strategies.
   *
   * @param context - The logging context to pass to each strategy
   */
  public log(context: LogContext): void {
    for (const strategy of this.strategies) {
      strategy.log(context);
    }
  }
}
